import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { CROP_WORK_VIEW, EVENTS_VIEW } from '../../../router/routes';
import { RootState } from '../../../store/store';
import { can, route } from '../../../services/utils';
import Work from '../../../interfaces/work';
import Event from '../../../interfaces/event';
import Field from '../../../interfaces/field';
import css from './index.module.css';

interface Props {
  works: Work[];
  events: Event[];
}

export default function DayInfo({ works, events }: Props) {
  const history = useHistory();
  const { t } = useTranslation();
  const tp = useCallback(
    (key: string) => t('pages.dashboard.calendar.'.concat(key)),
    [t]
  );
  const { farmUser, farmId, season } = useSelector((state: RootState) => ({
    farmUser: state.global.farmUser,
    farmId: state.global?.farm?.id,
    season: state.global?.season,
  }));
  const openEvent = (id: string) => {
    history.push(route(EVENTS_VIEW, { id }));
  };

  const WorkInfo = useCallback(({ work }: { work: Work }) => {
    const names = work.fields.map((field: Field) => field.name).join(', ');

    return (
      <span>
        {work.work.value} {names}, {work.treated_area} ha
      </span>
    );
  }, []);

  function workItem(work: Work, index: number) {
    let item = <WorkInfo work={work} />;

    const linkable = can(farmUser, { title: 'view work' });

    if (linkable) {
      item = (
        <Link
          to={route(CROP_WORK_VIEW, {
            farmId: farmId,
            season: season,
            id: work.id,
          })}
        >
          {item}
        </Link>
      );
    }

    return (
      <li
        className={linkable && css['linkable']}
        key={`works-list-item-${index}`}
      >
        {item}
      </li>
    );
  }

  return (
    <div className={css['info']}>
      {works?.length > 0 && (
        <>
          <h4 className={css['title']}>{tp('works')}</h4>
          <ol data-name="dashboard-calendar-works-list">
            {works.map((work: Work, index) => workItem(work, index))}
          </ol>
        </>
      )}

      {events?.length > 0 && (
        <>
          <h4 className={css['title']}>{tp('events')}</h4>
          <ul data-name="dashboard-calendar-events-list">
            {events.map((event: Event, index) => (
              <li
                key={`events-list-item-${index}`}
                onClick={() => openEvent(event.id)}
              >
                {event.logo?.length > 0 && (
                  <div className={css['logo']}>
                    <img src={event.logo[0]?.src} alt="" />
                  </div>
                )}
                <div className={css['description']}>
                  <div>{event.organizer}</div>
                  <div>
                    {event.place} |{' '}
                    {typeof event.time === 'string' && event.time}
                  </div>
                  <div>{event.title}</div>
                </div>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}
